/**
 * React Query hooks for service bookings + the unified guest schedule.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/core';
import { notify } from '@/shared/services';
import {
  getServiceBookings, getServiceBooking, createServiceBooking, cancelServiceBooking,
  moveServiceBooking, checkInServiceBooking, getGuestSchedule,
} from '../services/serviceBookingService';
import type { ServiceBookingInput } from '../types';

const BOOKINGS = 'service-bookings';
const SCHEDULE = 'guest-schedule';

export function useServiceBookings(filters?: { guest?: string; run?: string; booking?: string; status?: string; date?: string }) {
  return useQuery({
    queryKey: [BOOKINGS, filters ?? {}],
    queryFn: () => getServiceBookings(filters),
  });
}

/** One booking's detail — the reschedule dialog needs its product + current run. */
export function useServiceBooking(id: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [BOOKINGS, 'detail', id],
    queryFn: () => getServiceBooking(id as string),
    enabled: (options?.enabled ?? true) && !!id,
  });
}

/** The unified activities + services schedule for one guest (modal + conflicts). */
export function useGuestSchedule(guestId: string | undefined, dateFrom?: string, dateTo?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [SCHEDULE, guestId, dateFrom ?? null, dateTo ?? null],
    queryFn: () => getGuestSchedule(guestId as string, dateFrom, dateTo),
    enabled: (options?.enabled ?? true) && !!guestId,
  });
}

function useScheduleInvalidator() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: [BOOKINGS] });
    qc.invalidateQueries({ queryKey: [SCHEDULE] });
  };
}

export function useCreateServiceBooking() {
  const invalidate = useScheduleInvalidator();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (data: ServiceBookingInput) => createServiceBooking(data),
    onSuccess: () => { invalidate(); notify.success(t('services.bookings.added', 'Added to guest')); },
    onError: () => notify.error(t('services.bookings.addError', 'Could not add the service')),
  });
}

export function useCancelServiceBooking() {
  const invalidate = useScheduleInvalidator();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: string) => cancelServiceBooking(id),
    onSuccess: () => { invalidate(); notify.success(t('services.bookings.cancelled', 'Cancelled')); },
    onError: () => notify.error(t('services.bookings.cancelError', 'Could not cancel')),
  });
}

export function useMoveServiceBooking() {
  const invalidate = useScheduleInvalidator();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ id, newRunId }: { id: string; newRunId: string }) => moveServiceBooking(id, newRunId),
    onSuccess: () => { invalidate(); notify.success(t('services.bookings.moved', 'Rescheduled')); },
    onError: () => notify.error(t('services.bookings.moveError', 'Could not reschedule')),
  });
}

export function useCheckInServiceBooking() {
  const invalidate = useScheduleInvalidator();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: string) => checkInServiceBooking(id),
    onSuccess: () => { invalidate(); notify.success(t('services.bookings.checkedIn', 'Checked in')); },
    onError: () => notify.error(t('services.bookings.checkInError', 'Could not check in')),
  });
}
